/**
 * Gauge Block Component
 * Circular progress indicator for overall performance scores
 */

import { GaugeBlock as GaugeBlockType } from "@/types/interview-feedback.types";
import { processScore } from "@/utils/scoreUtils";
import { HelpCircle } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface GaugeBlockProps {
  block: GaugeBlockType;
}

export const GaugeBlock = ({ block }: GaugeBlockProps) => {
  const { helper, value, maxValue } = block;
  
  // Process score using shared utility
  const scoreData = processScore(value, maxValue);
  const angle = (scoreData.percentage / 100) * 360;

  return (
    <div className="space-y-6">
      {/* Helper */}
      {helper && (
        <div className="flex items-center justify-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <button className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <HelpCircle className="w-4 h-4" />
                <span>What does this mean?</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="max-w-xs">
              <p className="text-sm whitespace-normal">{helper}</p>
            </PopoverContent>
          </Popover>
        </div>
      )}

        {/* Circular Gauge */}
        <div className="flex items-center justify-center">
          <div className="relative w-48 h-48">
            {/* Background Circle */}
            <svg
              className="w-full h-full transform -rotate-90"
              viewBox="0 0 200 200"
            >
              {/* Background track */}
              <circle
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke="currentColor"
                strokeWidth="16"
                className="text-muted/20"
              />
              
              {/* Progress arc */}
              <circle
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke={scoreData.color}
                strokeWidth="16"
                strokeLinecap="round"
                strokeDasharray={`${(angle / 360) * 502.4} 502.4`}
                className="transition-all duration-1000 ease-out"
                style={{
                  filter: "drop-shadow(0 0 8px currentColor)",
                }}
              />
            </svg>

            {/* Center Text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div
                className="text-5xl font-bold transition-all duration-1000"
                style={{ color: scoreData.color }}
              >
                {scoreData.value.toFixed(1)}
              </div>
              <div className="text-sm text-muted-foreground">out of 10</div>
            </div>
          </div>
        </div>

      {/* Score Label */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/20">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: scoreData.color }}
          />
          <span className="text-sm font-medium text-foreground">
            {scoreData.label}
          </span>
        </div>
      </div>
    </div>
  );
};
